import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

export type VerifiedLinkSequenceSnapshot = {
    hasVerifier: boolean;
    settled: boolean;
    okPaths: string[];
};

export type VerifiedLinkVerifierPublication = {
    mountSeq: number;
    inBookOutlet: boolean;
    pending: boolean;
    okPaths: string[];
};

export type VerifiedLinkSequenceWriteApi = {
    registerVerifier: () => number;
    updateVerifier: (
        mountSeq: number,
        snapshot: Omit<VerifiedLinkVerifierPublication, "mountSeq">,
    ) => void;
    unregisterVerifier: (mountSeq: number) => void;
};

export const EMPTY_VERIFIED_LINK_SEQUENCE: VerifiedLinkSequenceSnapshot = {
    hasVerifier: false,
    settled: false,
    okPaths: [],
};

const NOOP_WRITE_API: VerifiedLinkSequenceWriteApi = {
    registerVerifier: () => 0,
    updateVerifier: () => undefined,
    unregisterVerifier: () => undefined,
};

const VerifiedLinkSequenceWriteContext = createContext<VerifiedLinkSequenceWriteApi>(NOOP_WRITE_API);
const VerifiedLinkSequenceReadContext = createContext<VerifiedLinkSequenceSnapshot>(
    EMPTY_VERIFIED_LINK_SEQUENCE,
);

function sameStringList(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
        return false;
    }
    return left.every((value, index) => value === right[index]);
}

function sameSnapshot(
    left: VerifiedLinkSequenceSnapshot,
    right: VerifiedLinkSequenceSnapshot,
): boolean {
    return (
        left.hasVerifier === right.hasVerifier
        && left.settled === right.settled
        && sameStringList(left.okPaths, right.okPaths)
    );
}

function pickActiveVerifier(
    publications: Iterable<VerifiedLinkVerifierPublication>,
): VerifiedLinkVerifierPublication | null {
    const all = [...publications];
    if (all.length === 0) {
        return null;
    }
    const inBook = all.filter((publication) => publication.inBookOutlet);
    const candidates = inBook.length > 0 ? inBook : all;
    return candidates.reduce((highest, current) => (
        current.mountSeq > highest.mountSeq ? current : highest
    ));
}

function snapshotFromActive(
    active: VerifiedLinkVerifierPublication | null,
): VerifiedLinkSequenceSnapshot {
    if (!active) {
        return EMPTY_VERIFIED_LINK_SEQUENCE;
    }
    if (active.pending) {
        return { hasVerifier: true, settled: false, okPaths: [] };
    }
    return { hasVerifier: true, settled: true, okPaths: active.okPaths };
}

/**
 * Page-level registry of LinkVerifier ok-path sequences.
 * Split write/read so verifiers do not re-render when the published snapshot changes.
 * Default (no provider) is the empty disabled snapshot; publish is a no-op.
 */
export function VerifiedLinkSequenceProvider({
    children,
}: {
    children: React.ReactNode;
}): JSX.Element {
    const nextMountSeq = useRef(0);
    const publications = useRef<Map<number, VerifiedLinkVerifierPublication>>(new Map());
    const [snapshot, setSnapshot] = useState<VerifiedLinkSequenceSnapshot>(
        EMPTY_VERIFIED_LINK_SEQUENCE,
    );

    const recompute = useCallback(() => {
        const next = snapshotFromActive(pickActiveVerifier(publications.current.values()));
        setSnapshot((previous) => (sameSnapshot(previous, next) ? previous : next));
    }, []);

    const writeApi = useMemo<VerifiedLinkSequenceWriteApi>(() => ({
        registerVerifier: () => {
            const mountSeq = nextMountSeq.current + 1;
            nextMountSeq.current = mountSeq;
            publications.current.set(mountSeq, {
                mountSeq,
                inBookOutlet: false,
                pending: true,
                okPaths: [],
            });
            recompute();
            return mountSeq;
        },
        updateVerifier: (mountSeq, data) => {
            const existing = publications.current.get(mountSeq);
            if (!existing) {
                return;
            }
            if (
                existing.inBookOutlet === data.inBookOutlet
                && existing.pending === data.pending
                && sameStringList(existing.okPaths, data.okPaths)
            ) {
                return;
            }
            publications.current.set(mountSeq, { mountSeq, ...data });
            recompute();
        },
        unregisterVerifier: (mountSeq) => {
            if (!publications.current.delete(mountSeq)) {
                return;
            }
            recompute();
        },
    }), [recompute]);

    return (
        <VerifiedLinkSequenceWriteContext.Provider value={writeApi}>
            <VerifiedLinkSequenceReadContext.Provider value={snapshot}>
                {children}
            </VerifiedLinkSequenceReadContext.Provider>
        </VerifiedLinkSequenceWriteContext.Provider>
    );
}

export function useVerifiedLinkSequence(): VerifiedLinkSequenceSnapshot {
    return useContext(VerifiedLinkSequenceReadContext);
}

export function useVerifiedLinkSequencePublisher(): VerifiedLinkSequenceWriteApi {
    return useContext(VerifiedLinkSequenceWriteContext);
}
